import { Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { FamilyService } from "src/FamilyGroup/services/family";
import InputLabelCustom from "src/shared/components/forms/InputLabelCustom";
import {
  capitalizeWords,
  cleanRut,
  formatRut,
  parseJwt,
  validateEmail,
  validateRutChilean,
} from "src/shared/global/helpers";
import ManageStorage from "src/shared/global/manageStorage";
import { userState } from "src/store/user/userState";
import { readFamilyTextFetchState } from "../atom";
import EmailService from "src/services/email/email";
import { encryptString } from "src/shared/global/cryptoJSUtilities";

const FormFamilyGroup = ({ loadFamilyGroup = () => {} }) => {
  const [values, setValues] = useState({
    email: "",
    rut: "",
    parentesco: null,
  });
  const token = ManageStorage.getValueFromStorage("tokenUser");
  const user = useRecoilValue(userState);

  const payload = token ? parseJwt(token) : null;

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ error: false, text: "" });

  const [errors, setErrors] = useState({
    email: [],
    rut: [],
    parentesco: [],
  });

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (errors.hasOwnProperty(name)) {
      setErrors({ ...errors, [name]: [] });
    }
    if (value === undefined) value = "";
    if (name === "parentesco" && value === null) {
      setErrors({ ...errors, parentesco: ["Parentesco es obligatorio"] });
    }

    if (name === "email") {
      if (!value) {
        setErrors({ ...errors, email: ["Email es obligatorio"] });
      }
      const isValid = validateEmail(value);
      if (!isValid.ok) {
        setErrors({ ...errors, email: ["Email no es válido"] });
      }
    }

    if (name === "rut") {
      if (!value) {
        setErrors({ ...errors, rut: ["RUT es obligatorio"] });
      }
      const isValid = validateRutChilean(cleanRut(value));
      if (!isValid.ok) {
        setErrors({ ...errors, rut: ["RUT no es válido"] });
      }
      value = formatRut(value);
    }

    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useRecoilCallback(
    ({ snapshot }) =>
      async (e, newValues, user) => {
        e.preventDefault();
        {
          setLoading(true);
          const { email, rut, parentesco } = newValues;

          if (!email || !rut || !parentesco) {
            setMessage({
              error: true,
              text: "Todos los campos son obligatorios.",
            });
            setErrors({
              email: !email ? ["Email es obligatorio"] : [],
              rut: !rut ? ["RUT es obligatorio"] : [],
              parentesco: !parentesco ? ["Parentesco es obligatorio"] : [],
            });
            setLoading(false);
            return;
          }

          const body = {
            tutRut: payload.Username,
            rutPaciente: rut,
            emailPaciente: email,
            parCorrel: parentesco.parCorrel,
          };

          const response = await FamilyService.addFamilyMember(body);
          if (response.data.estado === 0) {
            setMessage({ error: true, text: response.data.descripcion });
            setLoading(false);
            return;
          }
          if (response.data.estado === 1) {
            let htmlText = await snapshot.getPromise(
              readFamilyTextFetchState()
            );

            const fullName = `${user.payload.uswNombres} ${user.payload.uswApePat} ${user.payload.uswApeMat}`;
            let htmlBody = htmlText.replace(
              "{{name}}",
              capitalizeWords(fullName)
            );
            htmlBody = htmlBody.replace(
              "{{code}}",
              response.data.tutorCodConfirm
            );
            const encryptedRut = encryptString(payload.Username);
            
            const redirectUrl = new URL(
              `${window.location.origin}${process.env.REACT_APP_ROOT_SERVER_PATH}/login`
            );
            redirectUrl.searchParams.set(
              "redirect",
              `/paciente/editar/grupo-familiar?tut=${encodeURIComponent(
                encryptedRut
              )}`
            );

            htmlBody = htmlBody.replace("{{url}}", redirectUrl.toString());

            let bodyEmail = {
              to: email,
              body: htmlBody,
              subject: "Confirmación de vinculación al grupo familiar",
            };

            try {
              await EmailService.send(bodyEmail);
            } catch (error) {
              console.error("Error al enviar correo:", error);
            }

            setMessage({ error: false, text: response.data.descripcion });
            setValues({
              email: "",
              rut: "",
              parentesco: null,
            });
            loadFamilyGroup();
          }
          setLoading(false);
        }
      },
    []
  );

  const [tiposParentesco, setTiposParentesco] = useState([]);
  const loadTiposParentesco = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const release = snapshot.retain();
        setLoading(true);
        try {
          const tipos = await FamilyService.getTiposParentesco();
          setTiposParentesco(tipos.data);
        } catch (error) {
          // catchErrorApi(error);
        } finally {
          setLoading(false);
          release();
        }
      },
    []
  );

  useEffect(() => {
    loadTiposParentesco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <div>
        <Typography variant="subtitle2" fontWeight={600}>
          Vincular integrante al grupo familiar
        </Typography>
        <Typography variant="caption" color="textSecondary">
          El integrante debe tener una cuenta activa en la plataforma para poder
          vincularse. Si no tiene cuenta, deberá crearla antes de vincularse.
        </Typography>
      </div>
      <Grid container spacing={2}>
        <Grid item md={4} xs={12}>
          <InputLabelCustom htmlFor="email" text="Email (*)" />
          <TextField
            id="email"
            name="email"
            type="text"
            fullWidth
            size="small"
            value={values.email}
            onChange={handleChange}
            placeholder="Ej: usuario@ejemplo.com"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            autoComplete="off"
            inputProps={{ style: { fontSize: "0.85rem" } }}
            error={!!errors.email.length}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <InputLabelCustom htmlFor="rut" text="RUT (*)" />
          <TextField
            id="rut"
            name="rut"
            type="text"
            fullWidth
            size="small"
            value={values.rut}
            onChange={handleChange}
            placeholder="Ej: 16.000.000-0"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            autoComplete="off"
            inputProps={{ style: { fontSize: "0.85rem" } }}
            error={!!errors.rut.length}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <InputLabelCustom htmlFor="parentesco" text="Parentesco (*)" />
          <Autocomplete
            id="parentesco"
            size="small"
            fullWidth
            options={tiposParentesco}
            getOptionLabel={(option) => option.parNombre}
            value={values.parentesco}
            onChange={(_, newValue) =>
              setValues((prev) => ({ ...prev, parentesco: newValue }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Seleccione parentesco"
                inputProps={{
                  ...params.inputProps,
                  style: { fontSize: "0.85rem" },
                }}
                error={!!errors.parentesco.length}
              />
            )}
          />
        </Grid>
      </Grid>
      {message.text && (
        <Alert
          severity={message.error ? "error" : "success"}
          onClose={() => setMessage({ ...message, text: "" })}
          sx={{ mt: 2, fontSize: "0.85rem", textTransform: "lowercase" }}
        >
          <small>{message.text}</small>
          <br />
          {!message.error && (
            <small>
              Se envió un correo de notificación al nuevo integrante para que
              confirme su vinculación.
            </small>
          )}
        </Alert>
      )}
      <Typography variant="caption" fontWeight={600}>
        (*) Campos obligatorios
      </Typography>
      <Stack direction="row" spacing={1} mt={2} justifyContent="center">
        <LoadingButton
          type="submit"
          variant="contained"
          color="secondary"
          startIcon={<Add />}
          sx={{ textTransform: "none", fontSize: "0.9rem", py: 1 }}
          onClick={(e) => handleSubmit(e, values, user)}
          loading={loading}
          loadingPosition="start"
        >
          Agregar integrante
        </LoadingButton>
      </Stack>
    </div>
  );
};

export default FormFamilyGroup;
